import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryParams } from './hooks/useQueryParams.js'
import { useRecallTranscript } from './hooks/useRecallTranscript.js'
import { useDeepgram } from './hooks/useDeepgram.js' // fallback for browser testing
import { useInworldTTS } from './hooks/useInworldTTS.js'
import { useClawVoice } from './hooks/useClawVoice.js'
import { useAudioCapture } from './hooks/useAudioCapture.js'
import { useGate } from './hooks/useGate.js'
import { useTranscriptPipeline } from './hooks/useTranscriptPipeline.js'
import { BridgeUI } from './components/BridgeUI.js'

const CLAW_URL = (import.meta.env.VITE_CLAW_GATEWAY_URL as string) || 'https://ai.romea.ai'

const DEFAULTS: Record<string, Record<string, any>> = {
  romea: {
    translator: { default_state: 'open', wake_words: ['romea continue', 'romea resume'], sleep_words: ['romea pause', 'romea sleep', 'romea stop'], tts_voice_id: 'Carter', action_confirmation: false },
    instruction: { default_state: 'closed', wake_words: ['romea wake up', 'romeo wake up', 'romania wake up', 'romia wake up', 'hey romea', 'hey romeo', 'romea reply', 'romeo reply'], sleep_words: ['romea sleep', 'romeo sleep', 'romea stop', 'romea goodbye', 'romea bye'], tts_voice_id: 'Carter', action_confirmation: false },
    external: { default_state: 'closed', wake_words: ['romea wake up', 'romeo wake up', 'romania wake up', 'hey romea'], sleep_words: ['romea sleep', 'romeo sleep', 'romea stop'], tts_voice_id: 'Carter', action_confirmation: true },
  },
  crisalix: {
    external: { default_state: 'closed', wake_words: ['romea wake up'], sleep_words: ['romea sleep'], tts_voice_id: 'default', action_confirmation: true },
  },
}

export default function App() {
  const config = useQueryParams()
  const { workspace, meetingId, mode } = config

  const [modeConfig, setModeConfig] = useState<any>(null)
  const [status, setStatus] = useState<'initializing' | 'ready' | 'error'>('initializing')
  const [currentSpeaker, setCurrentSpeaker] = useState('')
  const [transcriptChunks, setTranscriptChunks] = useState<any[]>([])
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [llmBadge, setLlmBadge] = useState('')

  const isRespondingRef = useRef(false)

  // Load mode config — synchronously from DEFAULTS first, then try Claw
  // MUST be synchronous so gate initializes with correct defaultState
  const ws = DEFAULTS[workspace] || DEFAULTS.romea
  const initialModeConfig = ws[mode] || ws.external || ws.instruction || {}

  useEffect(() => {
    setModeConfig(initialModeConfig)
    setStatus('ready')
    // Try to fetch from Claw (best-effort, don't block)
    fetch(`${CLAW_URL}/api/mode-config?workspace=${workspace}&mode=${mode}`)
      .then(r => r.ok ? r.json() : null)
      .then(cfg => { if (cfg && !cfg.error) setModeConfig(cfg) })
      .catch(() => {})
  }, [workspace, mode]) // eslint-disable-line

  // Use initialModeConfig for gate (available on first render) — falls back to loaded config
  const activeModeConfig = modeConfig || initialModeConfig

  const gateConfig = useMemo(() => ({
    defaultState: activeModeConfig?.default_state === 'open' ? 'open' as const : 'closed' as const,
    wakeWords: activeModeConfig?.wake_words || ['romea wake up'],
    sleepWords: activeModeConfig?.sleep_words || ['romea sleep'],
    actionConfirmation: activeModeConfig?.action_confirmation || false,
  }), [activeModeConfig])

  const { state: gateState, processTranscript, setResponding, setAwaitingConfirmation, setClosed } = useGate(gateConfig)

  // Shared transcript handler — used by both Recall native WS and Deepgram fallback
  const onTranscript = useCallback((t: any) => {
    setTranscriptChunks((prev) => [...prev, { ...t, startMs: t.startMs ?? Date.now(), endMs: t.endMs ?? Date.now() }])
    setCurrentSpeaker(t.speaker)
    // Skip gate processing while responding — prevents echo/feedback loop
    if (!isRespondingRef.current) processTranscript(t.text)
  }, [processTranscript])

  // Recall native transcript WS — works inside bot, no API key, no mic permission needed
  const { isConnected: recallConnected } = useRecallTranscript(status === 'ready', onTranscript)

  // Deepgram fallback — only active in browser (when Recall WS not available)
  const { isConnected: dgConnected, sendAudio } = useDeepgram(
    status === 'ready' && !recallConnected,
    onTranscript
  )

  const sttConnected = recallConnected || dgConnected

  const { isConnected: ttsConnected, isPlaying: ttsPlaying, speak, stop: stopTTS } = useInworldTTS(status === 'ready')
  const { respond, cancel: cancelClaw } = useClawVoice()

  // Mic capture only needed for Deepgram fallback (browser testing)
  const onAudioFrame = useCallback((pcm: Int16Array) => { sendAudio(pcm) }, [sendAudio])
  const { isReady: audioReady, error: audioError } = useAudioCapture(status === 'ready' && !recallConnected, onAudioFrame)

  // Debounce timer for respond
  const respondTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // GATED: debounce 1.2s after last transcript, then respond
  useEffect(() => {
    if (gateState !== 'open' || isRespondingRef.current) return
    // Only trigger on final chunks
    const lastChunk = transcriptChunks[transcriptChunks.length - 1]
    if (!lastChunk) return

    if (respondTimerRef.current) clearTimeout(respondTimerRef.current)
    respondTimerRef.current = setTimeout(() => {
      if (gateState !== 'open' || isRespondingRef.current) return
      const recentText = transcriptChunks.slice(-5).map((c) => c.text).join(' ').trim()
      if (!recentText) return
      // Don't respond if it's ONLY a wake/sleep phrase (no actual question)
      const normalized = recentText.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim()
      const isOnlyCommand = gateConfig.wakeWords.concat(gateConfig.sleepWords)
        .some(w => normalized === w.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim())
      if (isOnlyCommand) return

      isRespondingRef.current = true
      setResponding()
      setIsSpeaking(true)
      const lastTurns = transcriptChunks.slice(-8).map((c) => `${c.speaker}: ${c.text}`).join('\n')

      // Buffer all text deltas — speak the FULL response in one TTS call
      // (calling speak() per word aborts the previous TTS fetch each time)
      let textBuffer = ''
      respond(workspace, meetingId, mode, recentText, { lastTurns }, (event) => {
        if (event.type === 'text-delta') {
          textBuffer += (event.data.text || '')
        } else if (event.type === 'tool-call') {
          if (gateConfig.actionConfirmation) {
            setAwaitingConfirmation()
            speak("I'd like to do this. Should I proceed?", activeModeConfig?.tts_voice_id)
          }
        } else if (event.type === 'complete' || event.type === 'error') {
          const fullText = textBuffer.trim().replace(/\n/g, ' ')
          if (fullText) speak(fullText, activeModeConfig?.tts_voice_id)
          setIsSpeaking(false)
          isRespondingRef.current = false
          // Never auto-close — gate stays open until explicit sleep word
          isRespondingRef.current = false
        }
      })
    }, 1200) // wait 1.2s of silence before responding
  }, [gateState, transcriptChunks, workspace, meetingId, mode, activeModeConfig, gateConfig.actionConfirmation, respond, speak, setResponding, setAwaitingConfirmation, setClosed])

  // Barge-in
  useEffect(() => {
    if (ttsPlaying && gateState === 'open') {
      const lastChunk = transcriptChunks[transcriptChunks.length - 1]
      if (lastChunk?.isFinal && lastChunk.text.split(' ').length > 3) {
        stopTTS()
        cancelClaw()
        isRespondingRef.current = false
      }
    }
  }, [transcriptChunks, ttsPlaying, gateState, stopTTS, cancelClaw])

  // Show last 6 chunks live (all interim + final) with speaker label
  const captions = useMemo(() => transcriptChunks.slice(-6).map((c) => `${c.speaker}: ${c.text}`), [transcriptChunks])

  if (status === 'initializing') return <div style={{ padding: '2rem', color: '#fff', background: '#0a0a0f', height: '100vh' }}>Loading...</div>

  return (
    <BridgeUI
      workspace={workspace}
      mode={mode}
      gateState={gateState}
      recallConnected={sttConnected}
      ttsConnected={ttsConnected}
      audioReady={audioReady}
      isSpeaking={isSpeaking || ttsPlaying}
      currentSpeaker={currentSpeaker}
      captions={captions}
      llmBadge={llmBadge}
      error={audioError}
    />
  )
}
